import { useTranslation } from 'react-i18next'
import { Alpha3Code } from 'i18n-iso-countries'

import { SearchAndFilters } from '../searchAndFilters/SearchAndFilters'
import { useCandidates } from '../candidatesContext/useCandidatesContext'
import { formatDate, formatRejectionReason } from '../../utils/utils'
import styles from '../CandidatesContainer.module.scss'

import { getCountryNameFromAlpha3Code } from '@percent/partner-dashboard/i18n/countries'
import {
  Badge,
  FlagAvatar,
  Flex,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip
} from '@percent/lemonade'

export function RejectedCandidatesTable() {
  const { t } = useTranslation()

  const { candidates, sort, navigateToCandidate, queryParams } = useCandidates()
  const isSearchActive = !!queryParams.searchTerm && queryParams.searchTerm.trim() !== ''

  const columnsHeaders: TableColumn[] = [
    { id: 'country', content: t('table.header.country'), props: { width: '10%' } },
    { id: 'organization', content: t('table.header.organization'), props: { width: '40%' } },
    { id: 'rejectionReason', content: t('table.header.rejectionReason'), props: { width: '35%' } },
    { id: 'submittedAt', content: t('table.header.submittedAt'), isSortable: true, props: { width: '15%' } }
  ]

  if (candidates.isLoading) {
    return <Loader />
  }

  const emptyTableTitle = isSearchActive
    ? t('grants.searchEmptyTable.title')
    : t('grants.candidatesInStateTable.emptyTable.title')
  const emptyTableDescription = isSearchActive ? '' : t('grants.candidatesInStateTable.emptyTable.description')
  const illustration = isSearchActive ? 'no-results' : 'no-grant-candidates'

  return (
    <TableProvider
      columns={columnsHeaders}
      totalResults={candidates.totalResults}
      isLoading={candidates.isLoading}
      handleRequestSort={sort.handleRequestSort}
      sortDirection={sort.direction}
      sortByColumn="submittedAt"
      illustration={illustration}
      emptyTableTitle={emptyTableTitle}
      emptyTableDescription={emptyTableDescription}
      className={styles.tableWrapper}
    >
      {(candidates.totalResults > 0 || queryParams.searchTerm) && (
        <Flex direction="column" data-testid="search-and-filters">
          <SearchAndFilters enabledFilters={{ organisationSearch: true, currentState: false }} />
        </Flex>
      )}
      <Table>
        <TableBody>
          {candidates.data?.map(({ id, organisation, createdAt, rejectionReason, rejectionReasonDetail }) => (
            <TableRow
              key={`candidate-${id}`}
              data-testid={`candidate-${id}`}
              onClick={() => navigateToCandidate(id)}
              className={styles.tableRow}
            >
              <TableCell inlineStyle={{ width: '150px' }}>
                <Flex align="center">
                  <Tooltip
                    content={getCountryNameFromAlpha3Code(organisation.countryCode as Alpha3Code)}
                    placement="right"
                  >
                    <div className={styles.tooltipContent}>
                      <FlagAvatar code3={organisation.countryCode as Alpha3Code} width="28px" />
                    </div>
                  </Tooltip>
                </Flex>
              </TableCell>
              <TableCell>{organisation.name}</TableCell>
              <TableCell>
                <Badge variant="default">
                  {formatRejectionReason({ rejectionReason, rejectionReasonDetail }, t) ?? t('typography.notAvailable')}
                </Badge>
              </TableCell>
              <TableCell>{formatDate(createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Flex direction="column" p="10px">
        <TablePagination
          totalResultsText={t('table.results', { count: candidates.totalResults ?? undefined })}
          previousPage={candidates.previousPage}
          nextPage={candidates.nextPage}
        />
      </Flex>
    </TableProvider>
  )
}
